/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineMail, HiOutlinePaperAirplane } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Column } from "../../ui";
import { useNewsletterContext } from "../../context/NewsletterContext";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
// @ts-ignore — NewsletterModal is the existing JSX modal kept as-is.
import NewsletterModal from "./NewsletterModal";

type Subscriber = {
  newsletter_id: number | string;
  email: string;
  createdAt?: string;
  [key: string]: unknown;
};

// The digest dispatcher expects a giant payload where only ONE of the
// `*Ids` fields is populated — matching the legacy switch on contentTitle.
const DIGEST_KEY_BY_TITLE: Record<string, string> = {
  Event: "eventIds",
  Attraction: "attractionIds",
  Party: "partyIds",
  Restaurant: "restaurantIds",
  Nightlife: "nightLifeIds",
  Music: "musicIds",
  Safari: "safariIds",
  "City Guide": "cityGuideIds",
  "Tour Package": "tourPackageIds",
  Hotel: "hotelIds",
  Classes: "classIds",
  Museum: "museumIds",
  Shopping: "shoppingIds",
  Food: "foodIds",
  Book: "bookIds",
  Neighborhood: "neighborhoodIds",
  Ministries: "ministryIds",
  Embassy: "embassyIds",
  Companies: "companyIds",
  "Getting Around": "gettingAroundIds",
  Art: "artIds",
  Movies: "movieIds",
  Dance: "danceIds",
  Cinema: "cinemaIds",
  News: "newsIds",
  Theater: "theaterIds",
  "Tips And Advice": "tipsAndAdviceIds",
  "Things To Do": "thingsToDoIds",
  "Best Of Africa": "BestOfAfricaIds",
};

const buildDigestPayload = (title: string, ids: (number | string)[]) => {
  const payload: Record<string, (number | string)[]> = {};
  for (const key of Object.values(DIGEST_KEY_BY_TITLE)) payload[key] = [];
  const activeKey = DIGEST_KEY_BY_TITLE[title];
  if (activeKey) payload[activeKey] = ids;
  return payload;
};

const Overview: React.FC = () => {
  const { newsletter, count, fetchNewsletter, sendNotification } =
    useNewsletterContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contentTitle, setContentTitle] = useState("");
  const [dataIds, setDataIds] = useState<(number | string)[]>([]);

  useEffect(() => {
    if (!newsletter) fetchNewsletter(page, pageSize);
  }, [newsletter]);

  const rows = useMemo<Subscriber[] | null>(() => {
    if (!Array.isArray(newsletter)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return newsletter as Subscriber[];
    return (newsletter as Subscriber[]).filter((s) =>
      s.email?.toString().toLowerCase().includes(q),
    );
  }, [newsletter, query]);

  const sendDailyDigest = async () => {
    setLoading(true);
    const response: any = await sendNotification(
      buildDigestPayload(contentTitle, dataIds),
    );
    setLoading(false);
    if (response?.status === 200) setShowModal(false);
  };

  const columns: Column<Subscriber>[] = [
    {
      header: "Email address",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineMail className="h-4 w-4" />
          </span>
          <span className="break-all font-medium text-gray-900">
            {row.email}
          </span>
        </div>
      ),
    },
    {
      header: "Date joined",
      cell: (row) =>
        row.createdAt ? (
          <span className="text-gray-600">
            {new Date(row.createdAt).toDateString()}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      header: "Time",
      cell: (row) =>
        row.createdAt ? (
          <span className="text-[11.5px] text-gray-500">
            {new Date(row.createdAt).toLocaleTimeString()}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
  ];

  return (
    <>
      <ListPage<Subscriber>
        title="Newsletter"
        subtitle="Send daily digests and manage your subscriber list."
        icon={<HiOutlineMail className="h-5 w-5" />}
        actions={[
          {
            label: "Send daily digest",
            variant: "primary",
            icon: <HiOutlinePaperAirplane className="h-3.5 w-3.5" />,
            onClick: () => setShowModal(true),
          },
        ]}
        rows={rows}
        columns={columns}
        rowKey={(r) => r.newsletter_id}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Search by email…",
          hint: "The latest content will be sent to subscribers.",
        }}
        pagination={{
          page,
          pageSize,
          total: count,
          onChange: ({ page: p, pageSize: ps }) => {
            setPage(p);
            setPageSize(ps);
            fetchNewsletter(p, ps);
          },
        }}
        empty="No subscribers yet."
      />

      {showModal ? (
        <NewsletterModal
          loading={loading}
          dataIds={dataIds}
          contentTitle={contentTitle}
          setDataIds={setDataIds}
          onSubmit={sendDailyDigest}
          setContentTitle={setContentTitle}
          onClose={() => {
            setShowModal(false);
            setLoading(false);
          }}
        />
      ) : null}
    </>
  );
};

export default Overview;
